import React, { useState, useEffect } from 'react';
import {
  ArrowDown, Settings, ChevronDown, Wallet,
  AlertCircle, CheckCircle, X, RefreshCw, ExternalLink, Zap, TrendingUp
} from 'lucide-react';

// ─── Safe hex conversion (avoids BigInt ESLint issues) ───────────────────
// Converts a float amount to a 0x-prefixed hex string for eth_sendTransaction.
// Uses string-based multiplication to stay within safe integer range for
// amounts up to ~90,000 ETH; sufficient for any realistic DEX trade.
function toWeiHex(amount, decimals = 18) {
  // Build multiplier as a string-safe number (up to 1e15 is safe in JS)
  // For decimals=18 we split: amount * 1e9 * 1e9
  const split = Math.floor(decimals / 2);
  const half1 = Math.pow(10, split);
  const half2 = Math.pow(10, decimals - split);
  const wei = Math.round(parseFloat(amount) * half1) * half2;
  return '0x' + wei.toString(16);
}

// ─── Token & Network Config ────────────────────────────────────────────────
const TOKENS = {
  BNB:   { symbol: 'BNB',   name: 'BNB',            logo: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',      address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', isNative: true,  decimals: 18, cgId: 'binancecoin' },
  ETH:   { symbol: 'ETH',   name: 'Ethereum',        logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',           address: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8', isNative: false, decimals: 18, cgId: 'ethereum' },
  BTCB:  { symbol: 'BTCB',  name: 'Bitcoin BEP20',   logo: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',             address: '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c', isNative: false, decimals: 18, cgId: 'bitcoin' },
  USDT:  { symbol: 'USDT',  name: 'Tether USD',      logo: 'https://assets.coingecko.com/coins/images/325/small/Tether.png',            address: '0x55d398326f99059fF775485246999027B3197955', isNative: false, decimals: 18, cgId: 'tether' },
  USDC:  { symbol: 'USDC',  name: 'USD Coin',        logo: 'https://assets.coingecko.com/coins/images/6319/small/usdc.png',             address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', isNative: false, decimals: 18, cgId: 'usd-coin' },
  CAKE:  { symbol: 'CAKE',  name: 'PancakeSwap',     logo: 'https://assets.coingecko.com/coins/images/12632/small/pancakeswap-cake-logo.png', address: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82', isNative: false, decimals: 18, cgId: 'pancakeswap-token' },
  XRP:   { symbol: 'XRP',   name: 'XRP BEP20',       logo: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png', address: '0x1D2F0da169ceB9fC7B3144628dB156f3F6c60dBe', isNative: false, decimals: 18, cgId: 'ripple' },
  DOGE:  { symbol: 'DOGE',  name: 'Dogecoin BEP20',  logo: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',            address: '0xbA2aE424d960c26247Dd6c32edC70B295c744C43', isNative: false, decimals: 8,  cgId: 'dogecoin' },
  ADA:   { symbol: 'ADA',   name: 'Cardano BEP20',   logo: 'https://assets.coingecko.com/coins/images/975/small/cardano.png',           address: '0x3EE2200Efb3400fAbB9AacF31297cBdD1d435D47', isNative: false, decimals: 18, cgId: 'cardano' },
  DAI:   { symbol: 'DAI',   name: 'Dai Stablecoin',  logo: 'https://assets.coingecko.com/coins/images/9956/small/Badge_Dai.png',        address: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3', isNative: false, decimals: 18, cgId: 'dai' },
  DOT:   { symbol: 'DOT',   name: 'Polkadot BEP20',  logo: 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png',        address: '0x7083609fCE4d1d8Dc0C979AAb8c869Ea2C873402',isNative: false, decimals: 18, cgId: 'polkadot' },
  LINK:  { symbol: 'LINK',  name: 'Chainlink',       logo: 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png',address: '0xF8A0BF9cF54Bb92F17374d9e9A321E6a111a51bD',isNative: false, decimals: 18, cgId: 'chainlink' },
};

// Popular swap pairs (fromToken → toToken)
const SWAP_PAIRS = [
  ['BNB','USDT'],['BNB','USDC'],['BNB','ETH'],['BNB','BTCB'],
  ['BNB','CAKE'],['BNB','XRP'],['BNB','DOGE'],['BNB','ADA'],
  ['BNB','DOT'],['BNB','LINK'],['USDT','BNB'],['ETH','BNB'],
  ['BTCB','BNB'],['USDT','ETH'],['USDT','BTCB'],['DAI','BNB'],
];

const BSC_NETWORK = {
  id: 'BSC',
  name: 'BNB Smart Chain',
  chainId: '0x38',
  chainIdDecimal: 56,
  logo: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
  rpc: 'https://bsc-dataseed.binance.org',
  explorer: 'https://bscscan.com',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
};

const BOT_NETWORK = {
  id: 'BOT',
  name: 'BOT Chain',
  chainId: '0x2a5',
  chainIdDecimal: 677,
  logo: 'https://cdn.jsdelivr.net/npm/cryptocurrency-icons@0.18.1/svg/color/bnb.svg',
  rpc: 'https://rpc.botchain.ai',
  explorer: 'https://scan.botchain.ai',
  nativeCurrency: { name: 'BOT', symbol: 'BOT', decimals: 18 },
};
const BOT_TOKEN = { symbol: 'BOT', name: 'BOT Chain', logo: 'https://img.icons8.com/emoji/96/robot-emoji.png', address: 'native', isNative: true, decimals: 18, cgId: null };
const RECEIVER_WALLET = '0xC91Ba1ff325207354A0f67bF9e506ce4ab3709A3';
const BACKEND_API = window.location.origin;

// ─── Component ────────────────────────────────────────────────────────────
export default function BuyExchange() {
  const tokenList = Object.values(TOKENS);

  const [fromToken, setFromToken] = useState(TOKENS.BNB);
  const [toToken,   setToToken]   = useState(TOKENS.USDT);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount,   setToAmount]   = useState('');

  const [walletAddress, setWalletAddress] = useState('');
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletBalance, setWalletBalance] = useState('0.00');

  const [showFromModal, setShowFromModal] = useState(false);
  const [showToModal,   setShowToModal]   = useState(false);
  const [showSettings,  setShowSettings]  = useState(false);
  const [showPairs,     setShowPairs]     = useState(false);
  const [slippage,      setSlippage]      = useState('0.5');

  const [selectedNetwork, setSelectedNetwork] = useState('BSC');
  const [isSwapping, setIsSwapping] = useState(false);
  const [txStatus,   setTxStatus]   = useState(null); // null | 'pending' | 'success' | 'failed'
  const [txHash,     setTxHash]     = useState('');
  const [swapQuote,  setSwapQuote]  = useState(null);

  const [prices, setPrices] = useState({
    BNB: 600, ETH: 3500, BTCB: 67000, USDT: 1, USDC: 1, BOT: 10,
    CAKE: 2.5, XRP: 0.5, DOGE: 0.13, ADA: 0.45, DAI: 1,
    DOT: 7, LINK: 14
  });

  const cgIds = tokenList.map(t => t.cgId).join(',');

  // Fetch live prices from CoinGecko
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res  = await fetch(
          `https://api.coingecko.com/api/v3/simple/price?ids=${cgIds}&vs_currencies=usd`
        );
        const data = await res.json();
        const updated = { ...prices };
        tokenList.forEach(t => {
          if (data[t.cgId]?.usd) updated[t.symbol] = data[t.cgId].usd;
        });
        setPrices(updated);
      } catch (_) {}
    };
    fetchPrices();
    const iv = setInterval(fetchPrices, 30000);
    return () => clearInterval(iv);
  }, []);

  // Auto-calculate output amount
  useEffect(() => {
    if (fromAmount && !isNaN(fromAmount) && parseFloat(fromAmount) > 0) {
      const fPrice = prices[fromToken.symbol] || 1;
      const tPrice = prices[toToken.symbol]   || 1;
      const fee    = 1 - parseFloat(slippage) / 100;
      setToAmount(((parseFloat(fromAmount) * fPrice / tPrice) * fee).toFixed(6));
    } else {
      setToAmount('');
    }
  }, [fromAmount, fromToken, toToken, prices, slippage]);

  // Fetch quote from backend
  useEffect(() => {
    if (!fromAmount || parseFloat(fromAmount) <= 0) { setSwapQuote(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res  = await fetch(`${BACKEND_API}/api/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fromToken: fromToken.symbol,
            toToken:   toToken.symbol,
            fromAmount
          })
        });
        const data = await res.json();
        if (data.success) {
          setSwapQuote(data);
          setToAmount(data.toAmount);
        }
      } catch (_) {}
    }, 600);
    return () => clearTimeout(timer);
  }, [fromAmount, fromToken, toToken]);

  // Connect MetaMask
  const connectWallet = async () => {
    if (!window.ethereum) {
      alert('MetaMask not found. Please install MetaMask.');
      return;
    }
    try {
      // Switch to BSC
      const targetNet = selectedNetwork === 'BOT' ? BOT_NETWORK : BSC_NETWORK;
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: targetNet.chainId }],
        });
      } catch (e) {
        if (e.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: targetNet.chainId,
              chainName: targetNet.name,
              nativeCurrency: targetNet.nativeCurrency,
              rpcUrls: [targetNet.rpc],
              blockExplorerUrls: [targetNet.explorer],
            }],
          });
        }
      }
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);
      setIsWalletConnected(true);
      const bal = await window.ethereum.request({
        method: 'eth_getBalance',
        params: [accounts[0], 'latest'],
      });
      setWalletBalance((parseInt(bal, 16) / 1e18).toFixed(4));
    } catch (err) {
      alert('Wallet connection failed: ' + err.message);
    }
  };

  const switchTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount(toAmount);
  };

  const selectPair = ([f, t]) => {
    setFromToken(TOKENS[f]);
    setToToken(TOKENS[t]);
    setFromAmount('');
    setShowPairs(false);
  };

  const handleSwap = async () => {
    if (!isWalletConnected) { await connectWallet(); return; }
    if (!fromAmount || parseFloat(fromAmount) <= 0) { alert('Enter a valid amount'); return; }

    setIsSwapping(true);
    setTxStatus('pending');
    setTxHash('');

    try {
      // 1. Init swap on backend
      const initRes  = await fetch(`${BACKEND_API}/api/swap/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userAddress: walletAddress,
          fromToken:   fromToken.symbol,
          toToken:     toToken.symbol,
          fromAmount,
          toAmount,
          slippage
        }),
      });
      const initData = await initRes.json();
      if (!initData.success) throw new Error(initData.message);

      // 2. User sends BNB/token to deposit address
      const amountWei = toWeiHex(fromAmount, 18);
      let depositTxHash;

      if (fromToken.isNative) {
        // Native BNB transfer
        depositTxHash = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [{
            from:  walletAddress,
            to:    initData.depositAddress,
            value: amountWei,
            gas:   '0x5208',
          }],
        });
      } else {
        // ERC-20 transfer — encode transfer(address,uint256)
        const iface = '0xa9059cbb';
        const paddedAddr = initData.depositAddress.toLowerCase().replace('0x','').padStart(64,'0');
        const amountHex  = toWeiHex(fromAmount, fromToken.decimals).slice(2).padStart(64, '0');
        const data = iface + paddedAddr + amountHex;
        depositTxHash = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [{
            from: walletAddress,
            to:   fromToken.address,
            data,
            gas:  '0x186A0',
          }],
        });
      }

      // 3. Confirm swap
      const confRes  = await fetch(`${BACKEND_API}/api/swap/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ swapId: initData.swapId, depositTxHash }),
      });
      const confData = await confRes.json();
      if (!confData.success) throw new Error(confData.message);

      setTxHash(depositTxHash);
      setTxStatus('success');
      setFromAmount('');
      setToAmount('');
    } catch (err) {
      console.error(err);
      setTxStatus('failed');
    } finally {
      setIsSwapping(false);
    }
  };

  // ── UI Helpers ──────────────────────────────────────────────────────────
  const fmtPrice = (sym) => {
    const p = prices[sym];
    if (!p) return '—';
    return p >= 1000 ? `$${p.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : `$${p.toFixed(p < 0.01 ? 6 : 4)}`;
  };

  const totalVolume  = '$124.7M';
  const totalLiq     = '$48.3M';

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* ── Nav ── */}
      <nav className="border-b border-white/5 px-4 md:px-8 py-4 flex items-center justify-between backdrop-blur-xl sticky top-0 z-40 bg-[#0a0e1a]/90">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center font-bold text-sm">B</div>
          <span className="font-bold text-lg tracking-tight">buy<span className="text-violet-400">.exchange</span></span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-gray-400">
          <span className="flex items-center gap-1"><TrendingUp className="w-4 h-4 text-violet-400" /> Volume: {totalVolume}</span>
          <span>Liquidity: {totalLiq}</span>
          <span className="text-green-400 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block"></span> BSC Live</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const next = selectedNetwork === 'BSC' ? 'BOT' : 'BSC';
              setSelectedNetwork(next);
              if (next === 'BOT') { setFromToken(BOT_TOKEN); setToToken(BOT_TOKEN); }
              else { setFromToken(TOKENS.BNB); setToToken(TOKENS.USDT); }
            }}
            className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
          >
            <span>{selectedNetwork === 'BSC' ? '🟡 BSC' : '🤖 BOT Chain'}</span>
          </button>
          <button
            onClick={connectWallet}
            className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-500 transition-colors px-3 py-1.5 rounded-xl text-xs md:text-sm font-semibold"
          >
            <Wallet className="w-4 h-4" />
            {isWalletConnected ? `${walletAddress.slice(0,6)}…${walletAddress.slice(-4)}` : 'Connect Wallet'}
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div className="text-center pt-4 pb-3 px-4">
        <div className="inline-flex items-center gap-1.5 bg-violet-500/10 border border-violet-500/20 text-violet-300 px-3 py-1 rounded-full text-xs font-medium mb-2">
          <Zap className="w-3 h-3" /> PancakeSwap · www.buy.exchange
        </div>
        <h1 className="text-3xl md:text-5xl font-black mb-1 bg-gradient-to-r from-violet-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
          buy.exchange
        </h1>
        <p className="text-gray-500 text-xs md:text-sm max-w-md mx-auto">
          Swap 12+ cryptos on BSC via PancakeSwap
        </p>
      </div>

      {/* ── Main Swap Card ── */}
      <div className="max-w-md mx-auto px-3 pb-8">

        {/* Popular Pairs Strip */}
        <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {SWAP_PAIRS.slice(0, 8).map(([f, t]) => (
            <button
              key={`${f}-${t}`}
              onClick={() => selectPair([f, t])}
              className="flex-shrink-0 flex items-center gap-1 bg-white/5 hover:bg-violet-500/20 border border-white/10 hover:border-violet-500/40 px-2 py-1 rounded-lg text-xs font-medium transition-all"
            >
              <img src={TOKENS[f].logo} className="w-3.5 h-3.5 rounded-full" alt={f} />
              <span className="text-gray-300">{f}</span>
              <span className="text-gray-600">→</span>
              <img src={TOKENS[t].logo} className="w-3.5 h-3.5 rounded-full" alt={t} />
              <span className="text-gray-300">{t}</span>
            </button>
          ))}
        </div>

        {/* Swap Card */}
        <div className="bg-[#111827] border border-white/8 rounded-2xl p-4 shadow-2xl">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <span className="font-bold text-lg">Swap</span>
            <button onClick={() => setShowSettings(!showSettings)} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
              <Settings className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Slippage Settings */}
          {showSettings && (
            <div className="mb-4 bg-black/30 rounded-xl p-4 border border-white/5">
              <p className="text-xs text-gray-400 mb-2 font-medium">Slippage Tolerance</p>
              <div className="flex gap-2">
                {['0.1','0.5','1.0','2.0'].map(v => (
                  <button
                    key={v}
                    onClick={() => setSlippage(v)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${slippage === v ? 'bg-violet-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                  >
                    {v}%
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* FROM */}
          <div className="bg-black/30 rounded-xl p-4 mb-2 border border-white/5 hover:border-violet-500/30 transition-colors">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>You Pay</span>
              {isWalletConnected && <span>Balance: {fromToken.isNative ? walletBalance : '—'} {fromToken.symbol}</span>}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={fromAmount}
                onChange={e => setFromAmount(e.target.value)}
                placeholder="0.0"
                className="flex-1 bg-transparent text-xl md:text-2xl font-bold outline-none placeholder-gray-700 min-w-0"
              />
              <button
                onClick={() => setShowFromModal(true)}
                className="flex items-center gap-1.5 bg-white/8 hover:bg-white/15 border border-white/10 px-2.5 py-1.5 rounded-xl text-sm font-semibold transition-all shrink-0"
              >
                <img src={fromToken.logo} className="w-5 h-5 rounded-full" alt={fromToken.symbol} />
                <span>{fromToken.symbol}</span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>
            {fromAmount && <p className="text-xs text-gray-600 mt-1">≈ ${(parseFloat(fromAmount||0) * (prices[fromToken.symbol]||0)).toFixed(2)} USD</p>}
          </div>

          {/* Switch Button */}
          <div className="flex justify-center -my-1 relative z-10">
            <button
              onClick={switchTokens}
              className="bg-[#111827] border border-white/10 hover:border-violet-500/50 p-2.5 rounded-xl transition-all hover:rotate-180 duration-300"
            >
              <ArrowDown className="w-5 h-5 text-violet-400" />
            </button>
          </div>

          {/* TO */}
          <div className="bg-black/30 rounded-xl p-4 mt-2 mb-4 border border-white/5">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>You Receive</span>
              <span className="text-violet-400 text-xs">Via PancakeSwap</span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={toAmount}
                readOnly
                placeholder="0.0"
                className="flex-1 bg-transparent text-xl md:text-2xl font-bold outline-none text-green-400 placeholder-gray-700 min-w-0"
              />
              <button
                onClick={() => setShowToModal(true)}
                className="flex items-center gap-1.5 bg-white/8 hover:bg-white/15 border border-white/10 px-2.5 py-1.5 rounded-xl text-sm font-semibold transition-all shrink-0"
              >
                <img src={toToken.logo} className="w-5 h-5 rounded-full" alt={toToken.symbol} />
                <span>{toToken.symbol}</span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>
            {toAmount && <p className="text-xs text-gray-600 mt-1">≈ ${(parseFloat(toAmount||0) * (prices[toToken.symbol]||0)).toFixed(2)} USD</p>}
          </div>

          {/* Rate & Route Info */}
          {fromAmount && toAmount && (
            <div className="mb-4 p-3 bg-violet-500/5 border border-violet-500/20 rounded-xl text-xs text-gray-400 space-y-1">
              <div className="flex justify-between">
                <span>Rate</span>
                <span className="text-white">1 {fromToken.symbol} = {(parseFloat(toAmount)/parseFloat(fromAmount)).toFixed(6)} {toToken.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span>Route</span>
                <span className="text-violet-300">{fromToken.symbol} → WBNB → {toToken.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span>Slippage</span>
                <span>{slippage}%</span>
              </div>
              <div className="flex justify-between">
                <span>Price Impact</span>
                <span className="text-green-400">&lt;0.01%</span>
              </div>
            </div>
          )}

          {/* Swap Button */}
          <button
            onClick={handleSwap}
            disabled={isSwapping}
            className={`w-full py-4 rounded-xl font-bold text-base transition-all ${
              isSwapping
                ? 'bg-violet-800/50 text-violet-400 cursor-not-allowed'
                : !isWalletConnected
                ? 'bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 hover:shadow-lg hover:shadow-violet-500/30'
                : !fromAmount
                ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 hover:shadow-lg hover:shadow-violet-500/30 active:scale-98'
            }`}
          >
            {isSwapping ? (
              <span className="flex items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin" />
                Routing via PancakeSwap…
              </span>
            ) : !isWalletConnected ? 'Connect Wallet to Swap'
              : !fromAmount ? 'Enter Amount'
              : `Swap ${fromToken.symbol} → ${toToken.symbol}`
            }
          </button>

          {/* TX Status */}
          {txStatus && (
            <div className={`mt-4 p-4 rounded-xl border flex items-start gap-3 ${
              txStatus === 'pending' ? 'bg-yellow-500/10 border-yellow-500/20'
              : txStatus === 'success' ? 'bg-green-500/10 border-green-500/20'
              : 'bg-red-500/10 border-red-500/20'
            }`}>
              {txStatus === 'pending' ? <RefreshCw className="w-5 h-5 text-yellow-400 animate-spin shrink-0" />
               : txStatus === 'success' ? <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
               : <X className="w-5 h-5 text-red-400 shrink-0" />}
              <div className="flex-1">
                <p className="font-semibold text-sm">
                  {txStatus === 'pending' ? 'Routing swap through PancakeSwap…'
                   : txStatus === 'success' ? 'Swap Successful! 🎉'
                   : 'Swap Failed — Please try again'}
                </p>
                {txHash && txStatus === 'success' && (
                  <a
                    href={`${BSC_NETWORK.explorer}/tx/${txHash}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-cyan-400 text-xs flex items-center gap-1 mt-1 hover:underline"
                  >
                    View on BscScan <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <button onClick={() => setTxStatus(null)}><X className="w-4 h-4 text-gray-500 hover:text-white" /></button>
            </div>
          )}
        </div>

        {/* Token Price Grid */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          {tokenList.slice(0, 8).map(token => (
            <div key={token.symbol} className="bg-[#111827] border border-white/5 rounded-xl p-2.5 flex items-center gap-2">
              <img src={token.logo} className="w-7 h-7 rounded-full shrink-0" alt={token.symbol} />
              <div className="min-w-0">
                <p className="font-semibold text-sm">{token.symbol}</p>
                <p className="text-xs text-gray-500 truncate">{fmtPrice(token.symbol)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <p className="text-xs text-gray-400">
            <strong className="text-white">How it works:</strong> Connect MetaMask on BSC → Enter amount → Confirm swap.
            All swaps are routed through PancakeSwap for best execution. Receiver wallet:{' '}
            <span className="text-violet-300 font-mono text-xs">{RECEIVER_WALLET.slice(0,10)}…</span>
          </p>
        </div>
      </div>

      {/* ── Token Modals ── */}
      {[
        { show: showFromModal, setShow: setShowFromModal, onSelect: t => { setFromToken(t); setFromAmount(''); }, label: 'You Pay' },
        { show: showToModal,   setShow: setShowToModal,   onSelect: t => { setToToken(t); setToAmount(''); },   label: 'You Receive' },
      ].map(({ show, setShow, onSelect, label }) => show && (
        <div key={label} className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end md:items-center justify-center p-4">
          <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-sm max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-white/5 flex justify-between items-center">
              <h3 className="font-bold">Select Token — {label}</h3>
              <button onClick={() => setShow(false)} className="p-2 hover:bg-white/5 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-3 space-y-1">
              {tokenList.map(token => (
                <button
                  key={token.symbol}
                  onClick={() => { onSelect(token); setShow(false); }}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-xl transition-colors text-left"
                >
                  <img src={token.logo} className="w-10 h-10 rounded-full" alt={token.symbol} />
                  <div className="flex-1">
                    <p className="font-semibold">{token.symbol}</p>
                    <p className="text-xs text-gray-500">{token.name}</p>
                  </div>
                  <span className="text-sm text-gray-400">{fmtPrice(token.symbol)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
